// Modal for creating and editing Inbound Webhook Workflows (WP-14).
import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import Toggle from '../ui/Toggle';
import {
  saveWebhookWorkflow,
  type WebhookWorkflowDoc,
  type WorkflowAction,
  type WorkflowActionType,
  WORKFLOW_ACTION_TYPES,
} from '../../lib/webhookWorkflows';
import { flowList } from '../../lib/flows';
import { sequenceList } from '../../lib/sequences';
import { labelList, listList } from '../../lib/crm';
import { useAccounts } from '../../lib/accounts';

interface WorkflowModalProps {
  open: boolean;
  onClose: () => void;
  workflow: WebhookWorkflowDoc | null;
  onSaved: () => void;
}

interface FieldMappingRow {
  key: string;
  jsonPath: string;
}

export function WorkflowModal({ open, onClose, workflow, onSaved }: WorkflowModalProps) {
  const { t } = useI18n();
  const { numbers } = useAccounts();

  const [name, setName] = useState('');
  const [integrationId, setIntegrationId] = useState('primary');
  const [phonePath, setPhonePath] = useState('$.customer.phone');
  const [extraMappings, setExtraMappings] = useState<FieldMappingRow[]>([]);
  const [actions, setActions] = useState<WorkflowAction[]>([]);
  const [enabled, setEnabled] = useState(true);

  // Entities for action dropdowns
  const [flows, setFlows] = useState<{ id: string; name: string }[]>([]);
  const [sequences, setSequences] = useState<{ id: string; name: string }[]>([]);
  const [labels, setLabels] = useState<{ id: string; name: string }[]>([]);
  const [lists, setLists] = useState<{ id: string; name: string }[]>([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Success view with newly minted credentials
  const [createdCredentials, setCreatedCredentials] = useState<{
    token?: string | null;
    secret?: string | null;
    url: string;
  } | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setCreatedCredentials(null);
      return;
    }

    // Load available automation entities
    Promise.all([
      flowList({}).catch(() => ({ data: { flows: [] } })),
      sequenceList({}).catch(() => ({ data: { sequences: [] } })),
      labelList({}).catch(() => ({ labels: [] })),
      listList({}).catch(() => ({ lists: [] })),
    ]).then(([flowRes, seqRes, labelRes, listRes]) => {
      setFlows(flowRes.data?.flows || []);
      setSequences(seqRes.data?.sequences || []);
      setLabels(labelRes.labels || []);
      setLists(listRes.lists || []);
    });

    if (workflow) {
      setName(workflow.name);
      setIntegrationId(workflow.integrationId || 'primary');
      setEnabled(workflow.enabled);

      const mapping = workflow.fieldMapping || {};
      setPhonePath(mapping.phone || '$.phone');

      const extra: FieldMappingRow[] = [];
      for (const [k, v] of Object.entries(mapping)) {
        if (k !== 'phone') {
          extra.push({ key: k, jsonPath: v });
        }
      }
      setExtraMappings(extra);
      setActions(workflow.actions || []);
    } else {
      setName('');
      setIntegrationId(numbers[0]?.id || 'primary');
      setPhonePath('$.customer.phone');
      setExtraMappings([
        { key: 'name', jsonPath: '$.customer.name' },
        { key: 'email', jsonPath: '$.customer.email' },
      ]);
      setActions([{ type: 'add_label', labelId: '' }]);
      setEnabled(true);
    }
    setError(null);
  }, [open, workflow]);

  const addMappingRow = () => {
    setExtraMappings([...extraMappings, { key: '', jsonPath: '$.' }]);
  };

  const removeMappingRow = (idx: number) => {
    setExtraMappings(extraMappings.filter((_, i) => i !== idx));
  };

  const updateMappingRow = (idx: number, field: keyof FieldMappingRow, val: string) => {
    const updated = [...extraMappings];
    updated[idx] = { ...updated[idx], [field]: val };
    setExtraMappings(updated);
  };

  const addActionRow = () => {
    setActions([...actions, { type: 'run_flow', flowId: flows[0]?.id || '' }]);
  };

  const removeActionRow = (idx: number) => {
    setActions(actions.filter((_, i) => i !== idx));
  };

  const updateAction = (idx: number, patch: Partial<WorkflowAction>) => {
    const updated = [...actions];
    updated[idx] = { ...updated[idx], ...patch };
    setActions(updated);
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(label);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      // noop
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('webhook.modal.nameRequired'));
      return;
    }
    if (!phonePath.trim()) {
      setError(t('webhook.modal.phoneRequired'));
      return;
    }
    if (actions.length === 0) {
      setError(t('webhook.modal.actionsRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const fieldMapping: Record<string, string> = {
        phone: phonePath.trim(),
      };
      for (const m of extraMappings) {
        if (m.key.trim() && m.jsonPath.trim()) {
          fieldMapping[m.key.trim()] = m.jsonPath.trim();
        }
      }

      const res = await saveWebhookWorkflow({
        id: workflow?.id,
        name: name.trim(),
        integrationId: integrationId.trim() || 'primary',
        fieldMapping,
        actions,
        enabled,
      });

      if (!workflow && (res.token || res.secret)) {
        // Show credentials view on creation
        setCreatedCredentials({
          token: res.token,
          secret: res.secret,
          url: res.url,
        });
        onSaved();
      } else {
        onSaved();
        onClose();
      }
    } catch (err: any) {
      setError(err.message || t('webhook.modal.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        createdCredentials
          ? t('webhook.modal.credentialsTitle')
          : workflow
          ? t('webhook.modal.editTitle')
          : t('webhook.modal.createTitle')
      }
    >
      {createdCredentials ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-emerald-50 p-3 text-xs text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
            {t('webhook.modal.createdSuccessNote')}
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <strong>{t('webhook.modal.warning')}:</strong> {t('webhook.modal.copyWarning')}
          </div>

          {/* Webhook Endpoint URL */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
              {t('webhook.modal.endpointUrl')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={createdCredentials.url}
                className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-mono dark:border-slate-700 dark:bg-slate-900"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => copyToClipboard(createdCredentials.url, 'url')}
              >
                {copiedKey === 'url' ? t('common.copied') : t('common.copy')}
              </Button>
            </div>
          </div>

          {/* Auth Token */}
          {createdCredentials.token && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                {t('webhook.modal.authToken')} (Bearer)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={createdCredentials.token}
                  className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-mono dark:border-slate-700 dark:bg-slate-900"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyToClipboard(createdCredentials.token!, 'token')}
                >
                  {copiedKey === 'token' ? t('common.copied') : t('common.copy')}
                </Button>
              </div>
            </div>
          )}

          {/* HMAC Secret */}
          {createdCredentials.secret && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                {t('webhook.modal.signingSecret')} (HMAC-SHA256)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={createdCredentials.secret}
                  className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-mono dark:border-slate-700 dark:bg-slate-900"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyToClipboard(createdCredentials.secret!, 'secret')}
                >
                  {copiedKey === 'secret' ? t('common.copied') : t('common.copy')}
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-3 border-t border-slate-200 dark:border-slate-800">
            <Button variant="primary" onClick={onClose}>
              {t('webhook.modal.doneButton')}
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto pe-1">
          {error && (
            <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          )}

          {/* Workflow Name */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
              {t('webhook.modal.name')} *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('webhook.modal.namePlaceholder')}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
            />
          </div>

          {/* WhatsApp Integration & Active Toggle */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
                {t('webhook.modal.integration')}
              </label>
              <select
                value={integrationId}
                onChange={(e) => setIntegrationId(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
              >
                <option value="primary">{t('webhook.modal.primaryNumber')}</option>
                {numbers.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.label || acc.verifiedPhoneE164 || acc.id}
                  </option>
                ))}
              </select>
            </div>

            <div className="pt-4 sm:pt-0">
              <Toggle on={enabled} onChange={setEnabled} label={t('webhook.modal.enabled')} />
            </div>
          </div>

          {/* Field Mapping Section */}
          <div className="border-t border-slate-200 pt-3 dark:border-slate-800 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 block">
                  {t('webhook.modal.fieldMappingTitle')}
                </label>
                <p className="text-[11px] text-slate-400">
                  {t('webhook.modal.fieldMappingDesc')}
                </p>
              </div>
              <button
                type="button"
                onClick={addMappingRow}
                className="text-xs font-medium text-primary hover:underline"
              >
                + {t('webhook.modal.addField')}
              </button>
            </div>

            {/* Subscriber Phone (Required) */}
            <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg dark:bg-slate-900/40">
              <div className="w-1/3">
                <span className="text-xs font-bold font-mono text-slate-700 dark:text-slate-200">
                  phone *
                </span>
                <span className="text-[10px] text-slate-400 block">({t('webhook.modal.requiredPhone')})</span>
              </div>
              <span className="text-slate-400 text-xs">←</span>
              <input
                type="text"
                required
                value={phonePath}
                onChange={(e) => setPhonePath(e.target.value)}
                placeholder="$.customer.phone"
                className="flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-mono"
              />
            </div>

            {/* Extra mappings */}
            {extraMappings.map((m, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={m.key}
                  onChange={(e) => updateMappingRow(idx, 'key', e.target.value)}
                  placeholder="name or order_id"
                  className="w-1/3 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-mono"
                />
                <span className="text-slate-400 text-xs">←</span>
                <input
                  type="text"
                  value={m.jsonPath}
                  onChange={(e) => updateMappingRow(idx, 'jsonPath', e.target.value)}
                  placeholder="$.customer.name"
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-mono"
                />
                <button
                  type="button"
                  onClick={() => removeMappingRow(idx)}
                  className="text-slate-400 hover:text-rose-500 text-base px-1"
                  title={t('common.delete')}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {/* Action Pipeline Builder */}
          <div className="border-t border-slate-200 pt-3 dark:border-slate-800 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 block">
                  {t('webhook.modal.actionsTitle')} *
                </label>
                <p className="text-[11px] text-slate-400">
                  {t('webhook.modal.actionsDesc')}
                </p>
              </div>
              <button
                type="button"
                onClick={addActionRow}
                className="text-xs font-medium text-primary hover:underline"
              >
                + {t('webhook.modal.addAction')}
              </button>
            </div>

            {actions.length === 0 ? (
              <p className="text-xs text-slate-400 italic bg-slate-50 p-3 rounded-lg dark:bg-slate-900/40">
                {t('webhook.modal.noActions')}
              </p>
            ) : (
              <div className="space-y-2">
                {actions.map((act, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900/60 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-slate-500">
                        {t('webhook.modal.step')} {idx + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeActionRow(idx)}
                        className="text-xs text-rose-500 hover:underline"
                      >
                        {t('common.delete')}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-slate-400 block mb-0.5">
                          {t('webhook.modal.actionType')}
                        </label>
                        <select
                          value={act.type}
                          onChange={(e) =>
                            updateAction(idx, { type: e.target.value as WorkflowActionType })
                          }
                          className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-medium"
                        >
                          {WORKFLOW_ACTION_TYPES.map((tKey) => (
                            <option key={tKey} value={tKey}>
                              {t(`webhook.actionType.${tKey}`)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Dynamic Parameters per action type */}
                      {act.type === 'run_flow' && (
                        <div>
                          <label className="text-[10px] text-slate-400 block mb-0.5">
                            {t('webhook.modal.selectFlow')}
                          </label>
                          <select
                            value={act.flowId || ''}
                            onChange={(e) => updateAction(idx, { flowId: e.target.value })}
                            className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
                          >
                            <option value="">{t('webhook.modal.chooseFlow')}</option>
                            {flows.map((f) => (
                              <option key={f.id} value={f.id}>
                                {f.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {(act.type === 'add_label' || act.type === 'remove_label') && (
                        <div>
                          <label className="text-[10px] text-slate-400 block mb-0.5">
                            {t('webhook.modal.selectLabel')}
                          </label>
                          <select
                            value={act.labelId || ''}
                            onChange={(e) => updateAction(idx, { labelId: e.target.value })}
                            className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
                          >
                            <option value="">{t('webhook.modal.chooseLabel')}</option>
                            {labels.map((l) => (
                              <option key={l.id} value={l.id}>
                                {l.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {act.type === 'subscribe_sequence' && (
                        <div>
                          <label className="text-[10px] text-slate-400 block mb-0.5">
                            {t('webhook.modal.selectSequence')}
                          </label>
                          <select
                            value={act.sequenceId || ''}
                            onChange={(e) => updateAction(idx, { sequenceId: e.target.value })}
                            className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
                          >
                            <option value="">{t('webhook.modal.chooseSequence')}</option>
                            {sequences.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {act.type === 'add_to_list' && (
                        <div>
                          <label className="text-[10px] text-slate-400 block mb-0.5">
                            {t('webhook.modal.selectList')}
                          </label>
                          <select
                            value={act.listId || ''}
                            onChange={(e) => updateAction(idx, { listId: e.target.value })}
                            className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
                          >
                            <option value="">{t('webhook.modal.chooseList')}</option>
                            {lists.map((lst) => (
                              <option key={lst.id} value={lst.id}>
                                {lst.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {act.type === 'send_template' && (
                        <div className="sm:col-span-2 grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-400 block mb-0.5">
                              {t('webhook.modal.templateName')}
                            </label>
                            <input
                              type="text"
                              value={act.templateName || ''}
                              onChange={(e) => updateAction(idx, { templateName: e.target.value })}
                              placeholder="order_confirmation"
                              className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-mono"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400 block mb-0.5">
                              {t('webhook.modal.templateLang')}
                            </label>
                            <input
                              type="text"
                              value={act.templateLanguage || 'en'}
                              onChange={(e) => updateAction(idx, { templateLanguage: e.target.value })}
                              placeholder="en"
                              className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Modal Buttons */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-800">
            <Button variant="ghost" type="button" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
